import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
} from 'firebase/firestore'
import { db, COLLECTIONS } from '@/lib/firebase'
import { getQueryDocsCacheFirst, subscribeQuery } from '@/lib/firestoreCache'
import type { Banner } from '@/types'

const col = collection(db, COLLECTIONS.banners)

function mapBanners(
  docs: { id: string; data: () => Record<string, unknown> }[]
): Banner[] {
  return docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as Banner[]
}

const bannersQuery = query(col, orderBy('order', 'asc'))

export async function fetchBanners(): Promise<Banner[]> {
  const docs = await getQueryDocsCacheFirst(bannersQuery)
  return mapBanners(docs)
}

/** Cache-first live updates (offline persistence). */
export function subscribeBanners(
  onData: (banners: Banner[]) => void,
  onError?: (error: Error) => void
) {
  return subscribeQuery(bannersQuery, mapBanners, onData, onError)
}

export async function createBanner(
  data: Omit<Banner, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const now = new Date().toISOString()
  const ref = await addDoc(col, { ...data, createdAt: now, updatedAt: now })
  return ref.id
}

export async function updateBanner(
  id: string,
  data: Partial<Banner>
): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.banners, id), {
    ...data,
    updatedAt: new Date().toISOString(),
  })
}

export async function deleteBanner(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.banners, id))
}
